import type { Flavor } from "@common/utils/type-utils";
import type { SpaceId } from "./space";
import type { UserId } from "./user";

export type TreeTodo = Todo & { children: TreeTodo[] };

export interface Todo {
  owner: UserId;
  id: TodoId;
  parent: TodoId | null; // parent id
  space: SpaceId; // null means it belongs to the default space of a user
  value: string;
  collapsed: boolean;
  completed: boolean;
  lexidx: string; // lexigraphical index
  date: number | null;
  time: number | null;
  duration: number | null; // estimated duration of this task in seconds
  // time: number; // estimated time in seconds, in "today"
  indent: number;
  lastEditDate: number;
  createdDate: number;
  dueDate: number | null;
  meta: MetaItem<MetaItemTypes>[] | null;
  tag: "important" | "urgent" | null;
  reminder: number | null;
  // soft delete
  deleted: boolean;
  notes_id: string | null;
}

export type IdentifiableTodoPartial = Partial<Todo> &
  Pick<Todo, "owner" | "space">; // a minimal partial that's required for it to be identifiable

export type TodoUpdateDelta = Partial<Omit<Todo, "owner" | "space">> &
  Pick<Todo, "id">;
export type TodoIdMap = { [id: TodoId]: Todo };

export type TodoUpdateDeltaIdMap = {
  [id: TodoId]: TodoUpdateDelta;
};

export type CreateTodoPayloadItem = {
  type: "CREATE";
  data: Todo;
  date: number;
};
export type UpdateTodoPayloadItem = {
  type: "UPDATE";
  data: TodoUpdateDelta;
  date: number;
};

export type TodoPayloadItem = CreateTodoPayloadItem | UpdateTodoPayloadItem;

export type TodoPayloadIdMap = {
  [id: TodoId]: TodoPayloadItem;
};

export type TodoId = Flavor<string, "TODO_ID">;
export type TodoListByGroupKey = { [groupKey: GroupKey]: Todo[] };

export type MetaItem<T> = {
  metaType: "HASHTAG" | "LINK";
  value: T;
};

// meta item types
export type HashtagMetaValueType = string;
export type LinkMetaValueType = { name?: string; link: string };
export type MetaItemTypes = LinkMetaValueType | HashtagMetaValueType;

export function isHashtagMetaItem(
  item: MetaItem<MetaItemTypes> | null | undefined
): item is MetaItem<HashtagMetaValueType> {
  if (!item) {
    return false;
  }
  return item.metaType === "HASHTAG";
}

export function getTodoHashtags(todo: Todo | null) {
  return todo?.meta?.filter(isHashtagMetaItem) ?? [];
}

export function getTodoHashtagsStr(todo: Todo | null) {
  return todo?.meta?.filter(isHashtagMetaItem).map((h) => h.value) ?? [];
}

export function isLinkMetaItem(
  item: MetaItem<MetaItemTypes>
): item is MetaItem<LinkMetaValueType> {
  return item.metaType === "LINK";
}

export type GroupKey = Flavor<string, "GROUP_KEY">;
